import React from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useSectionForm, useTenantSettings, useSaveSection } from "@/hooks/useTenantSettings";
import { useOptions } from "@/hooks/useOptions";
import { useAuth } from "@/context/AuthContext";
import { RegistrationPreview } from "@/components/settings/tenant/RegistrationPreview";
import { TextField, SelectField, TextareaField } from "@/components/kit/fields";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

export function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6">
      {title && <h3 className="mb-4 text-sm font-medium text-foreground">{title}</h3>}
      {children}
    </div>
  );
}

export function SaveBtn({ onClick, saving, testId }) {
  return (
    <Button onClick={onClick} disabled={saving} data-testid={testId} className="gap-2 bg-white text-black hover:bg-white/90">
      <Save className="h-4 w-4" /> Save changes
    </Button>
  );
}

const LANGS = ["English", "Russian", "Spanish", "German"];

export function OrgSettings() {
  const { form, set, submit, saving, loading } = useSectionForm("organization");
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="max-w-3xl space-y-5" data-testid="org-settings">
      <Card title="Partner network">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Partner network name" value={form.partner_network_name} onChange={(v) => set("partner_network_name", v)} testId="org-network-name" />
          <TextField label="Company legal name" value={form.legal_name} onChange={(v) => set("legal_name", v)} testId="org-legal-name" />
          <TextField label="Contact email" value={form.contact_email} onChange={(v) => set("contact_email", v)} testId="org-contact-email" />
          <TextField label="Support email" value={form.support_email} onChange={(v) => set("support_email", v)} testId="org-support-email" />
          <TextField label="Website" value={form.website} onChange={(v) => set("website", v)} testId="org-website" />
          <TextField label="Country" value={form.country} onChange={(v) => set("country", v)} testId="org-country" />
        </div>
      </Card>
      <Card title="Contacts">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TextField label="Telegram" value={form.telegram} onChange={(v) => set("telegram", v)} testId="org-telegram" />
          <TextField label="WhatsApp" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} testId="org-whatsapp" />
          <TextField label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testId="org-phone" />
          <TextField label="LinkedIn" value={form.linkedin} onChange={(v) => set("linkedin", v)} testId="org-linkedin" />
        </div>
      </Card>
      <Card title="Defaults">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SelectField label="Default language" value={form.default_language} onChange={(v) => set("default_language", v)} options={LANGS} testId="org-language" />
          <SelectField label="Manager contact visibility" value={form.manager_contact_visibility} onChange={(v) => set("manager_contact_visibility", v)}
            options={[{ value: "always", label: "Always visible" }, { value: "after_approval", label: "After approval" }, { value: "never", label: "Hidden" }]} testId="org-mgr-visibility" />
        </div>
        <div className="mt-4"><TextareaField label="Notes" value={form.notes} onChange={(v) => set("notes", v)} testId="org-notes" /></div>
      </Card>
      <SaveBtn onClick={() => submit("Organization saved")} saving={saving} testId="org-save" />
    </div>
  );
}

export function RegistrationSettings() {
  const { data, isLoading } = useTenantSettings();
  const save = useSaveSection();
  const opts = useOptions();
  const { user } = useAuth();
  const [reg, setReg] = React.useState(null);

  React.useEffect(() => {
    if (data?.sections?.registration) setReg(JSON.parse(JSON.stringify(data.sections.registration)));
  }, [data]);

  if (isLoading || !reg) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const branding = data?.sections?.branding || {};
  const org = data?.sections?.organization || {};

  const setF = (k, v) => setReg((f) => ({ ...f, [k]: v }));
  const setField = (i, key, val) => setReg((prev) => {
    const fields = prev.fields.map((x, j) => j === i
      ? { ...x, [key]: val, ...(key === "enabled" && !val ? { required: false } : {}) } : x);
    return { ...prev, fields };
  });
  const submit = async () => {
    await save.mutateAsync({ section: "registration", data: reg });
    toast.success("Registration settings saved");
  };

  const ToggleRow = ({ label, hint, k }) => (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3.5 py-3">
      <div><div className="text-sm text-foreground">{label}</div>{hint && <div className="text-xs text-muted-foreground">{hint}</div>}</div>
      <Switch checked={!!reg[k]} onCheckedChange={(v) => setF(k, v)} data-testid={`reg-${k}`} />
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2" data-testid="registration-settings">
      <div className="space-y-5">
        <Card title="Registration flow">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3.5 py-3">
              <div>
                <div className="text-sm text-foreground">Allow new affiliates to register</div>
                <div className="text-xs text-muted-foreground">Opens your public sign-up page. Turn off to close registration.</div>
              </div>
              <Switch checked={!!reg.registration_enabled && !!reg.allow_public_registration}
                onCheckedChange={(v) => setReg((f) => ({ ...f, registration_enabled: v, allow_public_registration: v }))}
                data-testid="reg-open" />
            </div>
            <ToggleRow label="Require manual approval" k="manual_approval"
              hint="New sign-ups stay on Hold until you approve them. Off = activated instantly." />
            <SelectField label="Auto-assign new affiliates to manager (optional)" value={reg.default_manager_id || "none"}
              onChange={(v) => setF("default_manager_id", v === "none" ? "" : v)}
              options={[{ value: "none", label: "— No auto-assignment —" },
                ...(opts.responsibles || []).map((m) => ({ value: m.id, label: m.id === user?.id ? `${m.name} (You)` : (m.role === "tenant_admin" ? `${m.name} · Tenant Admin` : m.name) }))]}
              testId="reg-default-manager" />
          </div>
        </Card>

        <Card title="Registration form fields">
          <p className="mb-3 text-xs text-muted-foreground">Pick which fields show on your sign-up form and which are required. Email &amp; password are always on. The preview updates instantly.</p>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_5rem_5rem] items-center gap-3 px-3 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span>Field</span><span className="text-center">Visible</span><span className="text-center">Required</span>
            </div>
            {(reg.fields || []).map((f, i) => (
              <div key={f.key} className="grid grid-cols-[1fr_5rem_5rem] items-center gap-3 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5 text-sm">
                <span className="text-foreground">{f.label}{f.locked && <span className="ml-2 text-[11px] text-muted-foreground">(always on)</span>}</span>
                <div className="flex justify-center"><input type="checkbox" data-testid={`reg-field-enabled-${f.key}`} disabled={f.locked} checked={!!f.enabled} onChange={(e) => setField(i, "enabled", e.target.checked)} className="h-4 w-4 accent-white disabled:opacity-40" /></div>
                <div className="flex justify-center"><input type="checkbox" data-testid={`reg-field-required-${f.key}`} disabled={f.locked || !f.enabled} checked={!!f.required} onChange={(e) => setField(i, "required", e.target.checked)} className="h-4 w-4 accent-white disabled:opacity-40" /></div>
              </div>
            ))}
          </div>
        </Card>

        <Button onClick={submit} disabled={save.isPending} data-testid="reg-save" className="gap-2 bg-white text-black hover:bg-white/90">
          <Save className="h-4 w-4" /> Save changes
        </Button>
      </div>

      <div className="xl:sticky xl:top-4 xl:self-start">
        <RegistrationPreview networkName={org.partner_network_name} logo={branding.logo_url}
          sideImage={branding.side_image_url} fields={(reg.fields || []).filter((f) => f.enabled)} />
      </div>
    </div>
  );
}

export function BrandingSettings() {
  const { form, set, submit, saving, loading } = useSectionForm("branding");
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="max-w-3xl space-y-5" data-testid="branding-settings">
      <Card title="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="Network name" value={form.network_name} onChange={(v) => set("network_name", v)} testId="brand-network-name" />
          <TextField label="Logo URL" value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="brand-logo" placeholder="https://…" />
          <TextField label="Favicon URL" value={form.favicon_url} onChange={(v) => set("favicon_url", v)} testId="brand-favicon" />
          <TextField label="Login page background URL" value={form.login_bg_url} onChange={(v) => set("login_bg_url", v)} testId="brand-login-bg" />
          <TextField label="Registration page background URL" value={form.registration_bg_url} onChange={(v) => set("registration_bg_url", v)} testId="brand-reg-bg" />
        </div>
      </Card>
      <Card title="Colors & text">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex items-end gap-2"><div className="flex-1"><TextField label="Primary color" value={form.primary_color} onChange={(v) => set("primary_color", v)} testId="brand-primary" /></div><span className="mb-1 h-9 w-9 rounded-md border border-white/10" style={{ background: form.primary_color }} /></div>
          <div className="flex items-end gap-2"><div className="flex-1"><TextField label="Accent color" value={form.accent_color} onChange={(v) => set("accent_color", v)} testId="brand-accent" /></div><span className="mb-1 h-9 w-9 rounded-md border border-white/10" style={{ background: form.accent_color }} /></div>
          <TextField label="Login page title" value={form.login_title} onChange={(v) => set("login_title", v)} testId="brand-login-title" />
          <TextField label="Registration page title" value={form.registration_title} onChange={(v) => set("registration_title", v)} testId="brand-reg-title" />
          <TextField label="Terms link" value={form.terms_link} onChange={(v) => set("terms_link", v)} testId="brand-terms" />
          <TextField label="Privacy policy link" value={form.privacy_link} onChange={(v) => set("privacy_link", v)} testId="brand-privacy" />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4">
          <TextareaField label="Support text" value={form.support_text} onChange={(v) => set("support_text", v)} testId="brand-support" />
          <TextField label="Footer text" value={form.footer_text} onChange={(v) => set("footer_text", v)} testId="brand-footer" />
        </div>
      </Card>
      <BrandingPreview form={form} />
      <SaveBtn onClick={() => submit("Branding saved")} saving={saving} testId="brand-save" />
    </div>
  );
}

function BrandingPreview({ form }) {
  const Logo = () => form.logo_url
    ? <img src={form.logo_url} alt="" className="h-6 max-w-[120px] object-contain" />
    : <span className="text-sm font-semibold" style={{ color: form.primary_color }}>{form.network_name || "Your Network"}</span>;
  return (
    <Card title="Preview">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="branding-preview">
        {[["Login page", form.login_title, form.login_bg_url], ["Registration page", form.registration_title, form.registration_bg_url]].map(([t, title, bg]) => (
          <div key={t} className="overflow-hidden rounded-lg border border-white/[0.08]">
            <div className="relative flex h-28 items-center justify-center bg-cover bg-center" style={{ backgroundImage: bg ? `url(${bg})` : "none", background: bg ? undefined : "#0A0A0A" }}>
              <div className="absolute inset-0 bg-black/50" />
              <div className="relative rounded-md border border-white/10 bg-[#141417]/90 px-4 py-2 text-center">
                <Logo /><div className="mt-1 text-xs text-muted-foreground">{title || t}</div>
              </div>
            </div>
            <div className="px-3 py-2 text-xs text-muted-foreground">{t}</div>
          </div>
        ))}
        <div className="overflow-hidden rounded-lg border border-white/[0.08]">
          <div className="flex h-28 flex-col">
            <div className="flex items-center gap-2 border-b border-white/[0.08] bg-[#0A0A0A] px-3 py-2"><span className="h-3 w-3 rounded-full" style={{ background: form.accent_color }} /><Logo /></div>
            <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">Header / sidebar name</div>
          </div>
          <div className="px-3 py-2 text-xs text-muted-foreground">In-app branding</div>
        </div>
      </div>
    </Card>
  );
}

export function DomainsSettings() {
  const { form, set, submit, saving, loading } = useSectionForm("domains");
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="max-w-2xl space-y-5" data-testid="domains-settings">
      <Card title="Tenant domains">
        <div className="space-y-4">
          <TextField label="Partner panel domain" value={form.panel_domain} onChange={(v) => set("panel_domain", v)} testId="dom-panel" placeholder="panel.network.com" />
          <TextField label="Registration domain" value={form.registration_domain} onChange={(v) => set("registration_domain", v)} testId="dom-reg" />
          <TextareaField label="Tracking domains (one per line)" value={form.tracking_domains} onChange={(v) => set("tracking_domains", v)} testId="dom-tracking" />
          <TextField label="Postback domain" value={form.postback_domain} onChange={(v) => set("postback_domain", v)} testId="dom-postback" />
          <TextField label="Default tracking domain" value={form.default_tracking_domain} onChange={(v) => set("default_tracking_domain", v)} testId="dom-default" />
        </div>
      </Card>
      <SaveBtn onClick={() => submit("Domains saved")} saving={saving} testId="dom-save" />
    </div>
  );
}
